import React, { useEffect, useState } from 'react'
import { TemplateEditorContextMenu, ContextMenuAction } from '../__canvas/templateEditor__contextMenu'
import { TemplateElement } from '../templateEditor.types'
import { buildLayerContextMenuGroups } from './templateEditor__buildLayerContextMenu'
import { buildLayerRows } from './templateEditor__buildLayerRows'
import { isParentGroupHidden, LayerRowItem } from './templateEditor__layerRow'
import styles from './templateEditor__leftPanels.jcss'

type LayersListProps = {
    elements: TemplateElement[]
    selectedIds: string[]
    onSelect: (id: string, additive: boolean) => void
    onToggleHidden: (id: string) => void
    onToggleLock: (id: string) => void
    onReorder: (fromIndex: number, toIndex: number) => void
    onReorderGroup: (groupId: string, fromVisualIndex: number, toVisualIndex: number) => void
    onBringToFront: (id: string) => void
    onSendToBack: (id: string) => void
    onBringForward: (id: string) => void
    onSendBackward: (id: string) => void
    onDuplicate: () => void
    onDelete: () => void
}

const parentGroupIdsOfSelection = (elements: TemplateElement[], selectedIds: string[]) => {
    const parents: string[] = []
    selectedIds.forEach((id) => {
        const groupId = elements.find((item) => item.id === id)?.groupId
        if (groupId && parents.indexOf(groupId) === -1) {
            parents.push(groupId)
        }
    })
    return parents
}

export const TemplateEditorLayersList = ({
    elements,
    selectedIds,
    onSelect,
    onToggleHidden,
    onToggleLock,
    onReorder,
    onReorderGroup,
    onBringToFront,
    onSendToBack,
    onBringForward,
    onSendBackward,
    onDuplicate,
    onDelete,
}: LayersListProps) => {
    const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([])
    const [dragFromIndex, setDragFromIndex] = useState<number | null>(null)
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
    const [groupDrag, setGroupDrag] = useState<{ groupId: string; fromVisual: number } | null>(null)
    const [groupDragOver, setGroupDragOver] = useState<{ groupId: string; visual: number } | null>(null)
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; groups: ContextMenuAction[][] } | null>(null)

    useEffect(() => {
        const parents = parentGroupIdsOfSelection(elements, selectedIds)
        if (parents.length === 0) {
            return
        }
        setExpandedGroupIds((prev) => {
            const missing = parents.filter((groupId) => prev.indexOf(groupId) === -1)
            return missing.length > 0 ? prev.concat(missing) : prev
        })
    }, [elements, selectedIds])

    const expandedSet = new Set(expandedGroupIds)
    const rows = buildLayerRows(elements, expandedSet)

    const clearDragState = () => {
        setDragFromIndex(null)
        setDragOverIndex(null)
        setGroupDrag(null)
        setGroupDragOver(null)
    }

    const toggleExpanded = (groupId: string) => {
        setExpandedGroupIds((prev) =>
            prev.indexOf(groupId) >= 0 ? prev.filter((id) => id !== groupId) : prev.concat(groupId)
        )
    }

    const openLayerContextMenu = (elementId: string, event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
        onSelect(elementId, false)
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            groups: buildLayerContextMenuGroups(
                elementId,
                {
                    bringToFront: onBringToFront,
                    sendToBack: onSendToBack,
                    bringForward: onBringForward,
                    sendBackward: onSendBackward,
                },
                { onDuplicate, onDelete }
            ),
        })
    }

    const drag = {
        fromIndex: dragFromIndex,
        overIndex: dragOverIndex,
        groupDrag,
        groupDragOver,
        setFromIndex: setDragFromIndex,
        setOverIndex: setDragOverIndex,
        setGroupDrag,
        setGroupDragOver,
        clear: clearDragState,
        onReorder,
        onReorderGroup,
    }

    return (
        <>
            <ul className={styles.layersList}>
                {rows.map((row) => (
                    <LayerRowItem
                        key={row.element.id}
                        row={row}
                        selected={selectedIds.includes(row.element.id)}
                        expanded={expandedSet.has(row.element.id)}
                        parentHidden={isParentGroupHidden(elements, row.groupId)}
                        drag={drag}
                        onSelect={onSelect}
                        onToggleHidden={onToggleHidden}
                        onToggleLock={onToggleLock}
                        onToggleExpanded={toggleExpanded}
                        onContextMenu={openLayerContextMenu}
                    />
                ))}
            </ul>
            {contextMenu && (
                <TemplateEditorContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    groups={contextMenu.groups}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </>
    )
}
